import { useState, useRef } from "react";
import { Download, Upload, Loader2, Check, FileJson } from "lucide-react";
import type { Character, CharacterExport } from "../services/api";
import { exportCharacter, importCharacterFile } from "../services/api";

interface Props {
  character: Character;
  onImported?: (char: Character) => void;
}

export function CharacterExportImport({ character, onImported }: Props) {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleExport() {
    setExporting(true);
    setStatus(null);
    try {
      const data = await exportCharacter(character.id);
      
      // 创建下载
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lokai_character_${character.name}_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setStatus({ type: "success", msg: `已导出 ${character.name}` });
    } catch (e: unknown) {
      setStatus({ type: "error", msg: e instanceof Error ? e.message : "导出失败" });
    } finally {
      setExporting(false);
      setTimeout(() => setStatus(null), 3000);
    }
  }

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setStatus(null);
    try {
      const char = await importCharacterFile(file);
      setStatus({ type: "success", msg: `已导入 ${char.name}` });
      onImported?.(char);
    } catch (e: unknown) {
      setStatus({ type: "error", msg: e instanceof Error ? e.message : "导入失败" });
    } finally {
      setImporting(false);
      // 清空 input 以便重复导入同一个文件
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setStatus(null), 4000);
    }
  }

  return (
    <div className="flex items-center gap-2">
      {/* 导出按钮 */}
      <button
        onClick={handleExport}
        disabled={exporting}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-400 hover:text-white hover:border-slate-600 transition-colors disabled:opacity-50"
        title="导出角色 (JSON)"
      >
        {exporting ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
        导出
      </button>

      {/* 导入按钮 */}
      <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-400 hover:text-white hover:border-slate-600 transition-colors cursor-pointer">
        {importing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Upload className="w-3.5 h-3.5" />
        )}
        导入
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          className="hidden"
        />
      </label>

      {/* 状态提示 */}
      {status && (
        <span
          className={`text-[11px] flex items-center gap-1 ${
            status.type === "success" ? "text-green-400" : "text-red-400"
          }`}
        >
          {status.type === "success" && <Check className="w-3 h-3" />}
          {status.msg}
        </span>
      )}
    </div>
  );
}

/** 用于角色库列表中的批量导入按钮 */
export function ImportCharacterButton({ onImported }: { onImported?: (char: Character) => void }) {
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setStatus(null);
    try {
      const char = await importCharacterFile(file);
      setStatus({ type: "success", msg: `已导入: ${char.name}` });
      onImported?.(char);
    } catch (e: unknown) {
      setStatus({ type: "error", msg: e instanceof Error ? e.message : "导入失败" });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setStatus(null), 4000);
    }
  }

  return (
    <div className="inline-flex items-center gap-2">
      <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs text-slate-400 hover:text-white hover:border-cyan-500/50 transition-colors cursor-pointer">
        {importing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <FileJson className="w-3.5 h-3.5" />
        )}
        导入角色
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          className="hidden"
        />
      </label>
      {status && (
        <span className={`text-[11px] ${status.type === "success" ? "text-green-400" : "text-red-400"}`}>
          {status.msg}
        </span>
      )}
    </div>
  );
}
